import { dayjs } from "@av/config/extended-dayjs";
import { DEFAULT_CURRENCY_CODE, type JobPostStats, type MockJobPostData } from "../../types";

const today = dayjs();
const defaultDescription =
  "You will own projects end to end: partnering with product and design on requirements, breaking work into iterative milestones, writing readable code with strong automated test coverage, and demoing early to gather feedback. You are expected to debug production issues thoughtfully, document decisions clearly, and champion healthy engineering practices such as observability, accessibility, and inclusive collaboration across time zones. In this role you will also influence technical direction by proposing architecture improvements, running proof-of-concepts, and helping the team make pragmatic trade-offs between speed and quality. You will pair regularly with teammates, review code with empathy, and invest in refactoring to keep the codebase maintainable and secure. You will collaborate with stakeholders to translate ambiguous business needs into actionable plans, manage risk by surfacing assumptions early, and provide clear status updates. You will mentor colleagues, share knowledge in design reviews, and contribute to documentation and onboarding guides so the whole team moves faster together.";
const defaultCountryCode = "CA";
const defaultRecruiter = { userId: "u16", displayName: "Concepción Peñalver" };
const defaultManager = { userId: "u1", displayName: "Nancy Smith" };

export const initialJobPosts: Array<MockJobPostData & JobPostStats> = [
  {
    companyId: "c1",
    jobPostId: "j1",
    jobId: 1,
    jobTitles: { "en-CA": "Developer II" },
    descriptions: { "en-CA": defaultDescription },
    postStatus: "POSTED",
    employmentType: "FULL_TIME",
    jobCountryCode: defaultCountryCode,
    currencyCode: DEFAULT_CURRENCY_CODE,
    payType: "SALARY",
    recruiters: [defaultRecruiter],
    hiringManagers: [defaultManager],
    applicationIds: ["CAND-001", "CAND-002"],
    isResumeRequired: true,
    isCoverLetterRequired: true,
    locationType: "HYBRID",
    subdivisionCode: "CA-BC",
    city: "Vancouver",
    postedDate: today.subtract(1, "days").toISOString(),
    applyBy: today.add(14, "days").toISOString(),
    minCompensation: 85000,
    maxCompensation: 105000,
    filledPositions: 0,
    totalPositions: 2,
    candidateCount: 129,
    nonTerminalCandidateCount: 0,
    pipeline: 12,
    includeCompensationOnJobPost: true,
    includeApplyByOnJobPost: true,
  },
  {
    companyId: "c1",
    jobPostId: "j2",
    jobId: 2,
    jobTitles: { "en-CA": "QA Engineer" },
    descriptions: { "en-CA": defaultDescription },
    postStatus: "POSTED",
    employmentType: "FULL_TIME",
    jobCountryCode: defaultCountryCode,
    currencyCode: DEFAULT_CURRENCY_CODE,
    payType: "SALARY",
    recruiters: [defaultRecruiter],
    hiringManagers: [defaultManager],
    applicationIds: ["CAND-003", "CAND-004", "CAND-005"],
    isResumeRequired: true,
    locationType: "REMOTE",
    subdivisionCode: "CA-ON",
    city: "Toronto",
    postedDate: today.subtract(1, "days").toISOString(),
    // No close date published: candidates see no "Accepting applications until" line.
    minCompensation: 75000,
    maxCompensation: 95000,
    filledPositions: 1,
    totalPositions: 2,
    candidateCount: 130,
    nonTerminalCandidateCount: 0,
    pipeline: 15,
    includeCompensationOnJobPost: true,
  },
  {
    companyId: "c1",
    jobPostId: "j3",
    jobId: 3,
    jobTitles: { "en-CA": "Frontend Developer" },
    descriptions: { "en-CA": defaultDescription },
    postStatus: "POSTED",
    employmentType: "FULL_TIME",
    jobCountryCode: defaultCountryCode,
    currencyCode: DEFAULT_CURRENCY_CODE,
    payType: "SALARY",
    recruiters: [defaultRecruiter],
    hiringManagers: [defaultManager],
    applicationIds: ["CAND-006", "CAND-007", "CAND-008", "CAND-009"],
    isResumeRequired: true,
    locationType: "REMOTE",
    subdivisionCode: "CA-AB",
    city: "Calgary",
    postedDate: today.subtract(3, "days").toISOString(),
    applyBy: today.add(18, "days").toISOString(),
    minCompensation: 80000,
    maxCompensation: 110000,
    filledPositions: 0,
    totalPositions: 3,
    candidateCount: 145,
    nonTerminalCandidateCount: 0,
    pipeline: 18,
    includeCompensationOnJobPost: true,
  },
  {
    companyId: "c1",
    jobPostId: "j4",
    jobId: 4,
    jobTitles: { "en-CA": "Developer" },
    descriptions: { "en-CA": defaultDescription },
    postStatus: "ARCHIVED",
    employmentType: "FULL_TIME",
    jobCountryCode: defaultCountryCode,
    currencyCode: DEFAULT_CURRENCY_CODE,
    payType: "SALARY",
    recruiters: [defaultRecruiter],
    hiringManagers: [defaultManager],
    applicationIds: ["CAND-010", "CAND-011", "CAND-012"],
    isResumeRequired: true,
    locationType: "IN_PERSON",
    subdivisionCode: "CA-QC",
    city: "Montreal",
    postedDate: today.subtract(6, "days").toISOString(),
    applyBy: today.add(12, "days").toISOString(),
    minCompensation: 70000,
    maxCompensation: 90000,
    filledPositions: 0,
    totalPositions: 2,
    candidateCount: 98,
    nonTerminalCandidateCount: 0,
    pipeline: 8,
    includeCompensationOnJobPost: true,
  },
  {
    companyId: "c1",
    jobPostId: "j5",
    jobId: 5,
    jobTitles: { "en-CA": "Developer" },
    descriptions: { "en-CA": defaultDescription },
    postStatus: "POSTED",
    employmentType: "FULL_TIME",
    jobCountryCode: defaultCountryCode,
    currencyCode: DEFAULT_CURRENCY_CODE,
    payType: "SALARY",
    recruiters: [defaultRecruiter],
    hiringManagers: [defaultManager],
    applicationIds: ["CAND-001", "CAND-003", "CAND-005"],
    isResumeRequired: true,
    locationType: "HYBRID",
    subdivisionCode: "CA-BC",
    city: "Victoria",
    postedDate: today.subtract(7, "days").toISOString(),
    // No close date published: candidates see no "Accepting applications until" line.
    minCompensation: 72000,
    maxCompensation: 92000,
    filledPositions: 1,
    totalPositions: 3,
    candidateCount: 156,
    nonTerminalCandidateCount: 0,
    pipeline: 20,
    includeCompensationOnJobPost: true,
  },
  {
    companyId: "c1",
    jobPostId: "j6",
    jobId: 6,
    jobTitles: { "en-CA": "Frontend Developer" },
    descriptions: { "en-CA": defaultDescription },
    postStatus: "POSTED",
    employmentType: "FULL_TIME",
    jobCountryCode: defaultCountryCode,
    currencyCode: DEFAULT_CURRENCY_CODE,
    payType: "SALARY",
    recruiters: [defaultRecruiter],
    hiringManagers: [defaultManager],
    applicationIds: ["CAND-002", "CAND-004"],
    isResumeRequired: true,
    locationType: "REMOTE",
    subdivisionCode: "CA-ON",
    city: "Ottawa",
    postedDate: today.subtract(10, "days").toISOString(),
    applyBy: today.add(8, "days").toISOString(),
    minCompensation: 82000,
    maxCompensation: 115000,
    filledPositions: 2,
    totalPositions: 4,
    candidateCount: 178,
    nonTerminalCandidateCount: 0,
    pipeline: 25,
    includeCompensationOnJobPost: true,
  },
  {
    companyId: "c1",
    jobPostId: "j7",
    jobId: 7,
    jobTitles: { "en-CA": "Backend Engineer" },
    descriptions: { "en-CA": defaultDescription },
    postStatus: "POSTED",
    employmentType: "FULL_TIME",
    jobCountryCode: defaultCountryCode,
    currencyCode: DEFAULT_CURRENCY_CODE,
    payType: "SALARY",
    recruiters: [defaultRecruiter],
    hiringManagers: [defaultManager],
    applicationIds: ["CAND-006", "CAND-008", "CAND-010"],
    isResumeRequired: true,
    locationType: "HYBRID",
    subdivisionCode: "CA-ON",
    city: "Kitchener",
    postedDate: today.subtract(15, "days").toISOString(),
    applyBy: today.add(9, "days").toISOString(),
    minCompensation: 90000,
    maxCompensation: 120000,
    filledPositions: 1,
    totalPositions: 2,
    candidateCount: 142,
    nonTerminalCandidateCount: 0,
    pipeline: 16,
    includeCompensationOnJobPost: true,
  },
  {
    companyId: "c1",
    jobPostId: "j8",
    jobId: 8,
    jobTitles: { "en-CA": "UI/UX Designer" },
    descriptions: { "en-CA": defaultDescription },
    postStatus: "ARCHIVED",
    employmentType: "FULL_TIME",
    jobCountryCode: defaultCountryCode,
    currencyCode: DEFAULT_CURRENCY_CODE,
    payType: "SALARY",
    recruiters: [defaultRecruiter],
    hiringManagers: [defaultManager],
    applicationIds: ["CAND-007", "CAND-009", "CAND-011"],
    isResumeRequired: true,
    locationType: "REMOTE",
    subdivisionCode: "CA-BC",
    city: "Kelowna",
    postedDate: today.subtract(8, "days").toISOString(),
    applyBy: today.add(13, "days").toISOString(),
    minCompensation: 78000,
    maxCompensation: 95000,
    filledPositions: 0,
    totalPositions: 1,
    candidateCount: 189,
    nonTerminalCandidateCount: 0,
    pipeline: 22,
    includeCompensationOnJobPost: true,
  },
  {
    companyId: "c1",
    jobPostId: "j9",
    jobId: 9,
    jobTitles: { "en-CA": "Data Analyst" },
    descriptions: { "en-CA": defaultDescription },
    postStatus: "POSTED",
    employmentType: "FULL_TIME",
    jobCountryCode: defaultCountryCode,
    currencyCode: DEFAULT_CURRENCY_CODE,
    payType: "SALARY",
    recruiters: [defaultRecruiter],
    hiringManagers: [defaultManager],
    applicationIds: ["CAND-012", "CAND-001", "CAND-002"],
    isResumeRequired: true,
    locationType: "HYBRID",
    subdivisionCode: "CA-MB",
    city: "Winnipeg",
    postedDate: today.subtract(12, "days").toISOString(),
    applyBy: today.add(11, "days").toISOString(),
    minCompensation: 68000,
    maxCompensation: 88000,
    filledPositions: 1,
    totalPositions: 2,
    candidateCount: 167,
    nonTerminalCandidateCount: 0,
    pipeline: 19,
    includeCompensationOnJobPost: true,
  },
  {
    companyId: "c1",
    jobPostId: "j10",
    jobId: 10,
    jobTitles: { "en-CA": "Project Manager" },
    descriptions: { "en-CA": defaultDescription },
    postStatus: "POSTED",
    employmentType: "FULL_TIME",
    jobCountryCode: defaultCountryCode,
    currencyCode: DEFAULT_CURRENCY_CODE,
    payType: "SALARY",
    recruiters: [defaultRecruiter],
    hiringManagers: [defaultManager],
    applicationIds: ["CAND-003", "CAND-004", "CAND-005", "CAND-006"],
    isResumeRequired: true,
    locationType: "IN_PERSON",
    subdivisionCode: "CA-AB",
    city: "Edmonton",
    postedDate: today.subtract(10, "days").toISOString(),
    applyBy: today.add(15, "days").toISOString(),
    minCompensation: 95000,
    maxCompensation: 115000,
    filledPositions: 1,
    totalPositions: 1,
    candidateCount: 201,
    nonTerminalCandidateCount: 0,
    pipeline: 28,
    includeCompensationOnJobPost: true,
    includeApplyByOnJobPost: true,
  },
  {
    companyId: "c1",
    jobPostId: "j11",
    jobId: 11,
    jobTitles: { "en-CA": "Product Manager" },
    descriptions: { "en-CA": defaultDescription },
    postStatus: "POSTED",
    employmentType: "FULL_TIME",
    jobCountryCode: defaultCountryCode,
    currencyCode: DEFAULT_CURRENCY_CODE,
    payType: "SALARY",
    recruiters: [defaultRecruiter],
    hiringManagers: [defaultManager],
    applicationIds: ["CAND-007", "CAND-008"],
    isResumeRequired: true,
    locationType: "HYBRID",
    subdivisionCode: "CA-ON",
    city: "Toronto",
    postedDate: today.subtract(6, "days").toISOString(),
    applyBy: today.add(17, "days").toISOString(),
    minCompensation: 100000,
    maxCompensation: 130000,
    filledPositions: 0,
    totalPositions: 2,
    candidateCount: 176,
    nonTerminalCandidateCount: 0,
    pipeline: 21,
    includeCompensationOnJobPost: true,
  },
  {
    companyId: "c1",
    jobPostId: "j12",
    jobId: 12,
    jobTitles: { "en-CA": "Mobile App Developer" },
    descriptions: { "en-CA": defaultDescription },
    postStatus: "DRAFT",
    employmentType: "FULL_TIME",
    jobCountryCode: defaultCountryCode,
    currencyCode: DEFAULT_CURRENCY_CODE,
    payType: "SALARY",
    recruiters: [defaultRecruiter],
    hiringManagers: [defaultManager],
    applicationIds: ["CAND-009", "CAND-010", "CAND-011"],
    isResumeRequired: true,
    locationType: "REMOTE",
    subdivisionCode: "CA-NS",
    city: "Halifax",
    postedDate: today.subtract(2, "days").toISOString(),
    applyBy: today.add(22, "days").toISOString(),
    minCompensation: 85000,
    maxCompensation: 110000,
    filledPositions: 0,
    totalPositions: 2,
    candidateCount: 112,
    nonTerminalCandidateCount: 0,
    pipeline: 9,
    includeCompensationOnJobPost: true,
  },
  {
    companyId: "c1",
    jobPostId: "j13",
    jobId: 13,
    jobTitles: { "en-CA": "Cloud Architect" },
    descriptions: { "en-CA": defaultDescription },
    postStatus: "ARCHIVED",
    employmentType: "FULL_TIME",
    jobCountryCode: defaultCountryCode,
    currencyCode: DEFAULT_CURRENCY_CODE,
    payType: "SALARY",
    recruiters: [defaultRecruiter],
    hiringManagers: [defaultManager],
    applicationIds: ["CAND-012", "CAND-001", "CAND-003", "CAND-005"],
    isResumeRequired: true,
    locationType: "HYBRID",
    subdivisionCode: "CA-ON",
    city: "Waterloo",
    postedDate: today.subtract(4, "days").toISOString(),
    applyBy: today.add(20, "days").toISOString(),
    minCompensation: 120000,
    maxCompensation: 150000,
    filledPositions: 0,
    totalPositions: 1,
    candidateCount: 198,
    nonTerminalCandidateCount: 0,
    pipeline: 27,
    includeCompensationOnJobPost: true,
  },
  {
    companyId: "c1",
    jobPostId: "j14",
    jobId: 14,
    jobTitles: { "en-CA": "Technical Writer" },
    descriptions: { "en-CA": defaultDescription },
    postStatus: "DRAFT",
    employmentType: "FULL_TIME",
    jobCountryCode: defaultCountryCode,
    currencyCode: DEFAULT_CURRENCY_CODE,
    payType: "SALARY",
    recruiters: [defaultRecruiter],
    hiringManagers: [defaultManager],
    applicationIds: ["CAND-002", "CAND-004", "CAND-006"],
    isResumeRequired: true,
    locationType: "REMOTE",
    subdivisionCode: "CA-SK",
    city: "Regina",
    postedDate: today.subtract(6, "days").toISOString(),
    applyBy: today.add(19, "days").toISOString(),
    minCompensation: 65000,
    maxCompensation: 80000,
    filledPositions: 0,
    totalPositions: 1,
    candidateCount: 87,
    nonTerminalCandidateCount: 0,
    pipeline: 6,
    includeCompensationOnJobPost: true,
  },
];
